import { IntrinsicsTranslator, Vector2, Vector3, facilityToDevice } from "@app/shared";
import { useCallback, useEffect, useState } from "react";
import { CheckboxInput, EnumSelect, NumericInput } from "../../../shared";
import { ChartBounds } from "../DataChart";
import { DataViewerProps } from "../DataViewer";
import { getRadarFrame, getVideoFrame } from "../ProjectWithData";
import { FusionChart, FusionChartOptions, FusionChartProps, RadarFrameWithOpacity, SelectedTarget, VideoFrameWithOpacity, ViewAxisOptions } from "./FusionChart";

function centerPoint(x1: number, y1: number, x2: number, y2: number): Vector2 {
    return new Vector2({ x: x1 + (x2 - x1) / 2, y: y1 + (y2 - y1) / 2 });
}

function bottomQuarterCenterPoint(x1: number, y1: number, x2: number, y2: number): Vector2 {
    const retVal = new Vector2({ x: x1 + (x2 - x1) / 2, y: y2 - (y2 - y1) / 4 });
    return retVal;
}

export const FusionViewer: React.FC<DataViewerProps> = ({ project, currentTimeS, onFrameChange }) => {
    const [renderData, setRenderData] = useState<FusionChartProps>({
        chartOptions: {
            renderRadarPoints: false,
            renderRadarTargets: true,
            renderBoundingBoxCenters: true,
            renderBoundingBoxBottomQuarters: false,
            renderLaneLines: false,
            pitch: project.deviceTiltDegrees,
            roll: project.deviceRollDegrees,
            fusionOffsetPitchDegrees: ((project.device?.fusionOffsetPitch ?? 0) * -180) / Math.PI,
            fusionOffsetRollDegrees: ((project.device?.fusionOffsetRoll ?? 0) * 180) / Math.PI,
            height: project.device?.height ?? 0,
            estimatedReturn: project.device?.fusionEstimatedReturnHeight ?? 0,
            viewAxis: ViewAxisOptions.Z,
        },
        videoFrames: [],
        radarFrames: [],
        translator: new IntrinsicsTranslator(),
        selectedTargets: [],
        onTargetSelect: () => {},
    });
    const [bounds, setBounds] = useState<ChartBounds>({ minX: -50, maxX: 50, minY: 0, maxY: 150 });
    const [prevFramesToRender, setPrevFramesToRender] = useState<number | undefined>(0);
    const [selectedTargets, setSelectedTargets] = useState<SelectedTarget[]>([]);
    const [calibrationPairs, setCalibrationPairs] = useState<Array<[number, number, number, number]>>([]);
    const [optimizationResults, setOptimizationResults] = useState<{
        modern?: { error: number; params: any };
        legacy?: { error: number; params: any };
    }>({});
    const [options, setOptions] = useState<FusionChartOptions>({
        renderRadarPoints: false,
        renderRadarTargets: true,
        renderBoundingBoxCenters: true,
        renderBoundingBoxBottomQuarters: false,
        renderLaneLines: false,
        pitch: project.deviceTiltDegrees,
        roll: project.deviceRollDegrees,
        fusionOffsetPitchDegrees: ((project.device?.fusionOffsetPitch ?? 0) * -180) / Math.PI, // Fusion calibration offset separate from accelerometer
        fusionOffsetRollDegrees: ((project.device?.fusionOffsetRoll ?? 0) * 180) / Math.PI, // Fusion calibration offset separate from accelerometer
        height: project.device?.height ?? 0,
        estimatedReturn: project.device?.fusionEstimatedReturnHeight ?? 0,
        viewAxis: ViewAxisOptions.Z,
    });

    function setViewAxis(val: ViewAxisOptions) {
        console.log("SELECTED AXIS", val);
        if (val === ViewAxisOptions.X) {
            setOptions({ ...options, viewAxis: ViewAxisOptions.X });
            setBounds({ minX: 0, maxX: 150, minY: -20, maxY: 20 });
        } else if (val === ViewAxisOptions.Y) {
            setOptions({ ...options, viewAxis: ViewAxisOptions.Y });
            setBounds({ minX: -100, maxX: 100, minY: -20, maxY: 20 });
        } else {
            setOptions({ ...options, viewAxis: ViewAxisOptions.Z });
            setBounds({ minX: -40, maxX: 40, minY: 0, maxY: 150 });
        }
    }

    const handleTargetSelect = useCallback((target: SelectedTarget) => {
        console.log("FusionViewer received target selection:", target);
        setSelectedTargets((prev) => {
            // Remove any existing target of the same type
            const filtered = prev.filter((t) => t.type !== target.type);
            return [...filtered, target];
        });
    }, []);

    function addCalibrationPair() {
        const radarTarget = selectedTargets.find((t) => t.type === "radar");
        const videoTarget = selectedTargets.find((t) => t.type === "video");

        if (radarTarget && videoTarget) {
            // Add UV coordinates and radar XY coordinates
            setCalibrationPairs((prev) => [
                ...prev,
                [
                    videoTarget.position.x, // UV x
                    videoTarget.position.y, // UV y
                    radarTarget.position.x, // Radar x
                    radarTarget.position.y, // Radar y (range)
                ],
            ]);
            setSelectedTargets([]); // Clear selection after adding pair
        }
    }

    function optimizeCalibration() {
        if (calibrationPairs.length < 3) {
            alert("Need at least 3 calibration pairs for optimization");
            return;
        }

        var translator = new IntrinsicsTranslator();
        const combinedPitch = options.pitch + options.fusionOffsetPitchDegrees;
        const combinedRoll = options.roll + options.fusionOffsetRollDegrees;
        translator.calculateFromParameters(true, options.height, combinedPitch, combinedRoll, options.estimatedReturn);

        console.log("Starting modern calibration optimization with", calibrationPairs.length, "pairs");

        // Calculate initial error
        const initialError = calculateCalibrationError(calibrationPairs, translator);
        console.log("Initial error:", initialError.toFixed(4), "meters");

        // Use the new simultaneous optimization method
        const optimizedParams = translator.optimizeCalibrationParameters(calibrationPairs);
        console.log("Optimized parameters found:", optimizedParams);

        // Apply optimized parameters and calculate final error
        translator.calculateFromParameters(true, optimizedParams.height, optimizedParams.pitch, optimizedParams.roll, optimizedParams.returnHeight);
        const finalError = calculateCalibrationError(calibrationPairs, translator);
        console.log("Final error:", finalError.toFixed(4), "meters");
        console.log("Improvement:", (((initialError - finalError) / initialError) * 100).toFixed(1), "%");

        // Store results for comparison
        setOptimizationResults((prev) => ({
            ...prev,
            modern: { error: finalError, params: optimizedParams },
        }));

        // Update options with optimized values (subtract accelerometer to get fusion offset)
        setOptions((prev) => ({
            ...prev,
            fusionOffsetPitchDegrees: optimizedParams.pitch - prev.pitch,
            fusionOffsetRollDegrees: optimizedParams.roll - prev.roll,
            height: optimizedParams.height,
            estimatedReturn: optimizedParams.returnHeight,
        }));
    }

    function optimizeCalibrationLegacy() {
        if (calibrationPairs.length < 3) {
            alert("Need at least 3 calibration pairs for optimization");
            return;
        }

        var translator = new IntrinsicsTranslator();
        const combinedPitch = options.pitch + options.fusionOffsetPitchDegrees;
        const combinedRoll = options.roll + options.fusionOffsetRollDegrees;
        translator.calculateFromParameters(true, options.height, combinedPitch, combinedRoll, options.estimatedReturn);

        console.log("Starting legacy calibration optimization with", calibrationPairs.length, "pairs");

        // Calculate initial error
        const initialError = calculateCalibrationError(calibrationPairs, translator);
        console.log("Initial error:", initialError.toFixed(4), "meters");

        // Find best pitch and roll
        const bestPitchRoll = translator.findBestPitchRollForDistance(calibrationPairs);
        console.log("Best pitch/roll found:", bestPitchRoll);

        // Find best height with the optimized pitch/roll
        translator.calculateFromParameters(true, options.height, bestPitchRoll.pitch, bestPitchRoll.roll, options.estimatedReturn);
        const bestHeight = translator.findBestHeight(calibrationPairs);
        console.log("Best height found:", bestHeight);

        // Apply final parameters and calculate final error
        translator.calculateFromParameters(true, bestHeight, bestPitchRoll.pitch, bestPitchRoll.roll, options.estimatedReturn);
        const finalError = calculateCalibrationError(calibrationPairs, translator);
        console.log("Final error:", finalError.toFixed(4), "meters");
        console.log("Improvement:", (((initialError - finalError) / initialError) * 100).toFixed(1), "%");

        const legacyParams = {
            pitch: bestPitchRoll.pitch,
            roll: bestPitchRoll.roll,
            height: bestHeight,
            returnHeight: options.estimatedReturn,
        };

        // Store results for comparison
        setOptimizationResults((prev) => ({
            ...prev,
            legacy: { error: finalError, params: legacyParams },
        }));

        // Update options with optimized values (subtract accelerometer to get fusion offset)
        setOptions((prev) => ({
            ...prev,
            fusionOffsetPitchDegrees: bestPitchRoll.pitch - prev.pitch,
            fusionOffsetRollDegrees: bestPitchRoll.roll - prev.roll,
            height: bestHeight,
        }));
    }

    function clearCalibrationData() {
        setCalibrationPairs([]);
        setSelectedTargets([]);
        setOptimizationResults({});
    }

    function calculateCalibrationError(pairs: Array<[number, number, number, number]>, translator: IntrinsicsTranslator): number {
        let totalError = 0;
        let validPairs = 0;

        for (const [u, v, radarX, radarY] of pairs) {
            try {
                const groundFromImage = translator.imageToGround(new Vector2({ x: u, y: v }));
                const groundFromRadar = translator.radarToGround(new Vector3({ x: radarX, y: radarY, z: 0 }));

                const dx = groundFromImage.x - groundFromRadar.x;
                const dy = groundFromImage.y - groundFromRadar.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                totalError += distance;
                validPairs++;
            } catch (e) {
                console.warn("Error calculating distance for pair:", [u, v, radarX, radarY], e);
            }
        }

        return validPairs > 0 ? totalError / validPairs : Infinity;
    }
    useEffect(() => {
        var translator = new IntrinsicsTranslator();

        const combinedPitch = options.pitch + options.fusionOffsetPitchDegrees;
        const combinedRoll = options.roll + options.fusionOffsetRollDegrees;
        translator.calculateFromParameters(true, options.height, combinedPitch, combinedRoll, options.estimatedReturn);
        project.pointTranslator = translator;
        renderData.translator = translator;
    }, [options]);

    //When time changes, get the video radar frame and update our chart data
    useEffect(() => {
        setRenderData((oldRenderData) => {
            var newRenderData = { ...oldRenderData } as FusionChartProps;
            newRenderData.selectedTargets = selectedTargets;
            newRenderData.onTargetSelect = handleTargetSelect;
            if (project.videoOverlays && (options.renderBoundingBoxCenters || options.renderBoundingBoxBottomQuarters)) {
                //Setup rendering for the overlays
                const videoFrame = getVideoFrame(project, currentTimeS);
                newRenderData.videoFrames = new Array<VideoFrameWithOpacity>();

                var opacity = 0;
                var opacityStep = 1 / ((prevFramesToRender ?? 0) + 1);
                for (var i = videoFrame - (prevFramesToRender ?? 0); i <= videoFrame; i++) {
                    const rects = project.videoOverlays[i] || [];

                    opacity += opacityStep;
                    if (i < 0 || !project.radarFrames) continue;
                    newRenderData.videoFrames.push({
                        opacity: opacity,
                        number: i,
                        uvPoints: rects.filter((r) => r.cla !== 8).map((r) => bottomQuarterCenterPoint(r.bbox[0], r.bbox[1], r.bbox[2], r.bbox[3])),
                    });
                }
            }

            if (project.radarFrames && (options.renderRadarPoints || options.renderRadarTargets)) {
                //Get frames we will be rendering for the current time
                const radarFrame = getRadarFrame(project, currentTimeS);
                newRenderData.radarFrames = new Array<RadarFrameWithOpacity>();

                opacity = 0;
                opacityStep = 1 / ((prevFramesToRender ?? 0) + 1);
                for (let i = radarFrame - (prevFramesToRender ?? 0); i <= radarFrame; i++) {
                    opacity += opacityStep;
                    if (i < 0 || !project.radarFrames) continue;
                    newRenderData.radarFrames.push({ opacity: opacity, number: i, frame: project.radarFrames[i] });
                }
            }

            options.viewAxis = options.viewAxis ?? ViewAxisOptions.X; // default to X view axis
            newRenderData.chartOptions = options;
            newRenderData.bounds = bounds;

            // Transform lane lines from facility coordinates to ground coordinates
            const facilityLaneLines = (project.device as any)?.facility?.laneLines ?? [];
            const devicePos = { x: project.device?.positionX ?? 0, y: project.device?.positionY ?? 0 };

            const transformedLaneLines = facilityLaneLines.map((laneLine: any) => {
                if (!laneLine.points || laneLine.points.length < 2) return laneLine;

                const transformedPoints = facilityToDevice(laneLine.points, devicePos, project.device?.headingDegrees ?? 0);

                return { ...laneLine, points: transformedPoints };
            });

            newRenderData.laneLines = transformedLaneLines;

            return newRenderData;
        });
    }, [project, currentTimeS, project.radarFrames, project.videoOverlays, prevFramesToRender, options, bounds, selectedTargets, handleTargetSelect]);
    /*
        const handleChangeFrame = useCallback((delta: number) => {
            onFrameChange((currentTimeS) + (1 / ((project.radarFps ?? 10) + (delta < 0 ? .5 : -.5))) * delta)
        }, [currentTimeS, project.radarFps, onFrameChange]);
    */
    if (!project.radarDataBinaryFile) {
        return <>No radar data was found.</>;
    } else if (project.radarDataBinaryFile && !project.radarFrames?.length) {
        return (
            <>
                Radar data was invalid or couldn't be parsed.
                <br />
                {project.radarParsingError}
            </>
        );
    }

    return (
        !!project.radarFrames?.length && (
            <>
                <FusionChart {...renderData!} />
                <div className="w-full flex flex-col gap-3 justify-content-center place-content-center place-items-center">
                    <div className="flex flex-row  justify-content-center place-content-center place-items-center">
                        <CheckboxInput label="Render Radar Targets" onChange={(e) => setOptions({ ...options, renderRadarTargets: e.target.checked })} checked={options.renderRadarTargets} />
                        &nbsp;&nbsp;&nbsp;
                        <CheckboxInput label="Render Bounding Box " onChange={(e) => setOptions({ ...options, renderBoundingBoxCenters: e.target.checked })} checked={options.renderBoundingBoxCenters} />
                        &nbsp;&nbsp;&nbsp;
                        <CheckboxInput label="Render Lane Lines" onChange={(e) => setOptions({ ...options, renderLaneLines: e.target.checked })} checked={options.renderLaneLines} />
                        &nbsp;&nbsp;&nbsp;
                        <EnumSelect enumType={ViewAxisOptions} value={options.viewAxis} onChange={(val) => setViewAxis(val ?? 0)} />
                    </div>
                    <div className="flex flex-row justify-content-center place-content-center place-items-center gap-4">
                        <NumericInput label="Pitch (accelerometer)" allowFloat={true} className="inline" value={options.pitch} readOnly={true} />
                        <NumericInput label="Fusion Offset Pitch" allowFloat={true} className="inline" value={options.fusionOffsetPitchDegrees} onChange={(val) => val !== undefined && setOptions({ ...options, fusionOffsetPitchDegrees: val })} />
                    </div>
                    <div className="flex flex-row justify-content-center place-content-center place-items-center gap-4">
                        <NumericInput label="Roll (accelerometer)" allowFloat={true} className="inline" value={options.roll} readOnly={true} />
                        <NumericInput label="Fusion Offset Roll" allowFloat={true} className="inline" value={options.fusionOffsetRollDegrees} onChange={(val) => val !== undefined && setOptions({ ...options, fusionOffsetRollDegrees: val })} />
                    </div>
                    <div className="flex flex-row  justify-content-center place-content-center place-items-center">
                        <NumericInput label="Device Height" allowFloat={true} className="inline" value={options.height} onChange={(val) => val !== undefined && setOptions({ ...options, height: val })} />
                        <NumericInput label="Estimated Return Height" allowFloat={true} className="inline" value={options.estimatedReturn} onChange={(val) => val !== undefined && setOptions({ ...options, estimatedReturn: val })} />
                        <NumericInput label="Prev Frames to Render" allowFloat={false} className="inline" value={prevFramesToRender} onChange={setPrevFramesToRender} />
                    </div>

                    {/* Target Selection and Calibration Section */}
                    <div className="flex flex-col gap-2 border-t pt-3 mt-3">
                        <h3 className="text-lg font-semibold">Target Calibration</h3>

                        <div className="flex flex-row gap-2">
                            <div className="text-sm">Selected: {selectedTargets.map((t) => `${t.type} (${t.position.x.toFixed(2)}, ${t.position.y.toFixed(2)})`).join(", ") || "None"}</div>
                        </div>

                        <div className="flex flex-row gap-2">
                            <button className="px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50" onClick={addCalibrationPair} disabled={selectedTargets.length !== 2 || !selectedTargets.find((t) => t.type === "radar") || !selectedTargets.find((t) => t.type === "video")}>
                                Add Pair ({calibrationPairs.length})
                            </button>

                            <button className="px-3 py-1 bg-green-500 text-white rounded disabled:opacity-50" onClick={optimizeCalibration} disabled={calibrationPairs.length < 3} title="Modern simultaneous optimization (Nelder-Mead)">
                                Optimize (Modern)
                            </button>

                            <button className="px-3 py-1 bg-green-600 text-white rounded disabled:opacity-50" onClick={optimizeCalibrationLegacy} disabled={calibrationPairs.length < 3} title="Legacy sequential optimization">
                                Optimize (Legacy)
                            </button>

                            <button className="px-3 py-1 bg-red-500 text-white rounded" onClick={clearCalibrationData}>
                                Clear All
                            </button>
                        </div>

                        {calibrationPairs.length > 0 && (
                            <div className="text-sm">
                                <div>Calibration pairs ({calibrationPairs.length}):</div>
                                <div className="max-h-32 overflow-y-auto">
                                    {calibrationPairs.map((pair, i) => (
                                        <div key={i} className="text-xs">
                                            Video: ({pair[0].toFixed(1)}, {pair[1].toFixed(1)}) → Radar: ({pair[2].toFixed(1)}, {pair[3].toFixed(1)})
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="text-xs text-gray-600">Click a radar target (red) and a video target (blue) to select them, then click "Add Pair" to create calibration data.</div>

                        {/* Optimization Results Comparison */}
                        {(optimizationResults.modern || optimizationResults.legacy) && (
                            <div className="border-t pt-3 mt-3">
                                <h4 className="font-semibold mb-2">Optimization Results</h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    {optimizationResults.modern && (
                                        <div className="border rounded p-2 bg-green-50">
                                            <div className="font-medium text-green-800">Modern (Nelder-Mead)</div>
                                            <div>Error: {optimizationResults.modern.error.toFixed(4)}m</div>
                                            <div className="text-xs mt-1">
                                                P: {optimizationResults.modern.params.pitch.toFixed(2)}°, R: {optimizationResults.modern.params.roll.toFixed(2)}°, H: {optimizationResults.modern.params.height.toFixed(2)}m, RH: {optimizationResults.modern.params.returnHeight.toFixed(3)}m
                                            </div>
                                        </div>
                                    )}
                                    {optimizationResults.legacy && (
                                        <div className="border rounded p-2 bg-blue-50">
                                            <div className="font-medium text-blue-800">Legacy (Sequential)</div>
                                            <div>Error: {optimizationResults.legacy.error.toFixed(4)}m</div>
                                            <div className="text-xs mt-1">
                                                P: {optimizationResults.legacy.params.pitch.toFixed(2)}°, R: {optimizationResults.legacy.params.roll.toFixed(2)}°, H: {optimizationResults.legacy.params.height.toFixed(2)}m, RH: {optimizationResults.legacy.params.returnHeight.toFixed(3)}m
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {optimizationResults.modern && optimizationResults.legacy && (
                                    <div className="mt-2 text-sm">
                                        {optimizationResults.modern.error < optimizationResults.legacy.error ? (
                                            <span className="text-green-600 font-medium">Modern method is {(((optimizationResults.legacy.error - optimizationResults.modern.error) / optimizationResults.legacy.error) * 100).toFixed(1)}% better</span>
                                        ) : (
                                            <span className="text-blue-600 font-medium">Legacy method is {(((optimizationResults.modern.error - optimizationResults.legacy.error) / optimizationResults.modern.error) * 100).toFixed(1)}% better</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </>
        )
    );
};
